import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMenuPermissions } from '../contexts/MenuPermissionsContext';

const apps = [
  {
    id: 'crm',
    label: 'CRM',
    description: 'Contatos, funis, automações e WhatsApp',
    path: '/contatos',
    color: 'from-blue-600 to-indigo-700'
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Marketing Studio, agentes e criativos',
    path: '/marketing-studio',
    color: 'from-fuchsia-600 to-violet-700'
  },
  {
    id: 'seller',
    label: 'Seller',
    description: 'Métricas, agentes e automação de vendas',
    path: '/seller',
    color: 'from-orange-700 to-orange-900'
  },
  {
    id: 'business-core',
    label: 'Business Core',
    description: 'Produtos e base de conhecimento da empresa',
    path: '/business-core',
    color: 'from-emerald-600 to-teal-700'
  },
  {
    id: 'flow',
    label: 'Flow',
    description: 'Tarefas, documentos e agenda da equipe',
    path: '/flow',
    color: 'from-sky-700 to-cyan-900'
  }
];

function getCurrentApp(pathname: string) {
  if (pathname.startsWith('/marketing-studio')) return 'Marketing';
  if (pathname.startsWith('/business-core')) return 'Business Core';
  if (pathname.startsWith('/seller')) return 'Seller';
  if (pathname.startsWith('/flow')) return 'Flow';
  return 'CRM';
}

type PlatformSwitcherProps = {
  compact?: boolean;
};

export function PlatformSwitcher({ compact = false }: PlatformSwitcherProps) {
  const location = useLocation();
  const { canSeePlatform } = useMenuPermissions();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentApp = getCurrentApp(location.pathname);
  const visibleApps = apps.filter(app => canSeePlatform(app.id));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={`relative ${open ? '' : 'platform-switcher-attention'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex h-8 items-center gap-2 rounded-full border border-indigo-300 bg-white px-2.5 text-sm shadow-md transition hover:border-indigo-500 hover:bg-indigo-50"
        title="Trocar plataforma"
        aria-describedby="platform-switcher-hint"
      >
        {!compact && <span className="font-semibold text-slate-700">{currentApp}</span>}
        <span className="grid h-6 w-6 grid-cols-3 gap-0.5 rounded-lg bg-slate-100 p-1">
          {Array.from({ length: 9 }).map((_, index) => (
            <span key={index} className="rounded-sm bg-slate-600" />
          ))}
        </span>
      </button>

      {!open && (
        <span
          id="platform-switcher-hint"
          className="platform-switcher-hint pointer-events-none absolute right-0 top-full mt-1 whitespace-nowrap text-[13px] font-bold tracking-wide text-red-600"
        >
          Use outras funções
        </span>
      )}

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
          <div className="mb-3 px-1">
            <p className="text-sm font-bold text-slate-900">Escolha a plataforma</p>
            <p className="text-xs text-slate-500">Alterne entre as áreas da IntegraIA.</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {visibleApps.map(app => {
              const active = app.label === currentApp;
              return (
                <Link
                  key={app.id}
                  to={app.path}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-4 rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:shadow-lg ${active ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white'}`}
                >
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${app.color} text-lg font-black text-white shadow-md`}>
                    {app.label.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{app.label}</p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-500">{app.description}</p>
                  </div>
                  {active && <span className="ml-auto text-indigo-500 text-xs font-bold">✓</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
